import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import axios from "axios";
import ReusableTable from "../../components/UI/ReusableTable";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { AuthContext } from "../../contexts/AuthContext";
import Modal from "../../components/UI/Modal";
import DeleteButton from "../../components/UI/AlertDialog";
import Drawer from "../../components/UI/Drawer";
import CampaignDetails from "../../components/Details/CampaignDetails";
import { User, Briefcase, MapPin, DollarSign, Mail, Phone, Clock, GraduationCap, Star, FileText, Download, CheckCircle, Building, Target, MoreVertical, AlertTriangle, Calendar, CreditCard, IndianRupee } from "lucide-react";
import { createPortal } from 'react-dom';
import { getToken } from "../../utils/tokenUtils";
import { FaRupeeSign } from "react-icons/fa";
import { Link } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";

// Custom Alert Dialog Component
const AlertDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-end md:items-center z-[1001] backdrop-blur-sm">
      <div className="bg-white dark:bg-black p-6 rounded-t-lg md:rounded-lg shadow-lg w-full max-w-sm mx-auto transition-transform duration-300">
        <h2 className="text-lg font-semibold text-center dark:text-white">Are you sure?</h2>
        <p className="text-gray-600 mt-2 text-center dark:text-gray-500">This action cannot be undone.</p>
        <div className="mt-4 flex justify-between space-x-3">
          <button
            className="block w-full py-2 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-10 py-2 text-white bg-red-600 rounded hover:bg-red-700 w-full"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Application Details Component
const ApplicationDetails = ({ application, close, onEdit, onDelete, onViewResume }) => {
  if (!application) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">No application selected</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="mx-auto bg-white shadow-2xl overflow-hidden">
      {/* Header Section */}
      <div className="bg-blue-500 px-8 py-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-semibold mb-2">{application.full_name}</h1>
            <div className="flex items-center space-x-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <Briefcase className="h-5 w-5" />
                <span>{application.campaign_name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>{application.current_location}</span>
              </div>
            </div>
          </div>
          <button
            onClick={close}
            className="text-white dark:hover:text-gray-400 hover:bg-blue-800 hover:text-gray-200 text-xl px-2 py-1 rounded-md transition delay-100">
            ✖
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        {/* Key Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-blue-500 p-2 rounded-lg flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <span className="text-blue-600 font-semibold">Experience</span>
            </div>
            <p className="text-md font-medium text-gray-900">{application.total_experience_years} years</p>
          </div>

          <div className="bg-green-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-green-500 p-2 rounded-lg flex-shrink-0">
                <Building className="h-5 w-5 text-white" />
              </div>
              <span className="text-green-600 font-semibold">Current Company</span>
            </div>
            <p className="text-md font-medium text-gray-900">{application.current_company_name || 'Not specified'}</p>
          </div>

          <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-purple-500 p-2 rounded-lg flex-shrink-0">
                <IndianRupee className="h-5 w-5 text-white" />
              </div>
              <span className="text-purple-600 font-semibold">Expected CTC</span>
            </div>
            <p className="text-md font-medium text-gray-900">₹{application.expected_ctc_month || '0'}/month</p>
          </div>

          <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <span className="text-orange-600 font-semibold">Contact</span>
            </div>
            <div className="space-y-1">
              <p className="text-md font-medium text-gray-900">{application.email || 'Not specified'}</p>
              <p className="text-md font-medium text-gray-900">{application.phone_number || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <User className="h-6 w-6 text-blue-600" />
            <span>Personal Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <GraduationCap className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Education:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.highest_education}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Date of Birth:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.date_of_birth ? formatDate(application.date_of_birth) : 'Not specified'}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Notice Period:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.notice_period}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">PAN Number:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.pan_number || 'Not specified'}</span>
            </div>
          </div>
        </div>

        {/* Professional Details */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Briefcase className="h-6 w-6 text-green-600" />
            <span>Professional Details</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Current Company:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.current_company_name}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <FaRupeeSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Current CTC:</span>
              </div>
              <span className="text-gray-700 ml-4">₹{application.current_ctc_month}/month</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-green-600" />
                <span className="font-medium text-gray-900">Current Designation:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.current_designation}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <FaRupeeSign className="h-4 w-4 text-green-600" />
                <span className="font-medium text-gray-900">Expected CTC:</span>
              </div>
              <span className="text-gray-700 ml-4">₹{application.expected_ctc_month}/month</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Target className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Position Applied:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.position_applied_for}</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Source:</span>
              </div>
              <span className="text-gray-700 ml-4">{application.source_of_application}</span>
            </div>
          </div>
        </div>

        {/* SWOT Analysis */}
        {/* {application.swot && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <FileText className="h-6 w-6 text-orange-600" />
              <span>SWOT Analysis</span>
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">{application.swot}</p>
            </div>
          </div>
        )} */}

        {/* Ideal Candidate */}
        {/* {application.ideal_candidate && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <User className="h-6 w-6 text-indigo-600" />
              <span>Ideal Candidate Description</span>
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">{application.ideal_candidate}</p>
            </div>
          </div>
        )} */}

        {/* Preferences */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <CheckCircle className="h-6 w-6 text-yellow-600" />
            <span>Preferences</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-gray-900">Comfortable with Travel:</span>
              </div>
              <span className="text-gray-700 ml-4">
                {application.comfortable_travel === 1 || application.comfortable_travel === true ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-gray-900">Comfortable with Six Days:</span>
              </div>
              <span className="text-gray-700 ml-4">
                {application.comfortable_six_days === 1 || application.comfortable_six_days === true ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Resume */}
        {(application.resume || application.resume_url) && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <button
                onClick={async () => {
                  try {
                    const resumeUrl = application.resume_url || application.resume;
                    const token = localStorage.getItem('authToken');
                    const response = await fetch(resumeUrl, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                      },
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to fetch resume');
                    }
                    
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const fileName = `resume_${application?.full_name?.replace(/\s+/g, '_') || 'application'}_${Date.now()}.pdf`;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Error downloading resume:', error);
                    // Fallback: create a link and trigger download
                    const a = document.createElement('a');
                    a.href = application.resume_url || application.resume;
                    a.download = `resume_${application?.full_name?.replace(/\s+/g, '_') || 'application'}_${Date.now()}.pdf`;
                    a.target = '_blank';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }
                }}
                className="hover:opacity-80 transition-opacity cursor-pointer"
                title="Download Resume"
              >
                <Download className="h-6 w-6 text-red-600" />
              </button>
              <span>Resume</span>
              <button
                onClick={() => onViewResume(application.resume_url || application.resume)}
                className="ml-3 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                View Resume
              </button>
            </h3>
          </div>
        )}

        {/* Timeline Footer */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <span>Submitted: {formatDate(application.created_at)}</span>
              {application.updated_at && application.updated_at !== application.created_at && (
                <>
                  <span>•</span>
                  <span>Updated: {formatDate(application.updated_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Bottom Right */}
        {(onEdit || onDelete) && (
          <div className="mt-6 flex justify-end gap-3">
            {onEdit && (
              <button
                onClick={() => {
                  onEdit(application.id);
                  close();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            )}
            {/* Delete option commented out - removed from details popup action buttons */}
            {/* {onDelete && (
              <button
                onClick={() => {
                  onDelete(application.id);
                  close();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

const ApplicationResponses = () => {
  const { user, roles } = useContext(AuthContext) || {};
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editFormErrors, setEditFormErrors] = useState({});
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isCampaignDrawerOpen, setIsCampaignDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const resumeIframeRef = useRef(null);
  const isInitialMount = useRef(true);
  const editResumeInputRef = useRef(null);
  const [shouldDeleteResume, setShouldDeleteResume] = useState(false);

  // Dropdown management
  const handleClickOutside = useCallback((event) => {
    // Check if click is outside dropdown container and dropdown menu
    if (
      !event.target.closest(".dropdown-container") &&
      !event.target.closest(".dropdown-menu")
    ) {
      setOpenDropdownId(null);
    }
  }, []);

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates - position exactly below the button
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 150; // min-w-[150px]
      const dropdownHeight = 120; // estimated height
      
      // Position dropdown directly below the button, left-aligned
      let x = rect.left;
      let y = rect.bottom + 4; // 4px gap below button
      
      // Check if dropdown would go off-screen to the right
      if (x + dropdownWidth > window.innerWidth) {
        x = window.innerWidth - dropdownWidth - 8; // 8px margin from edge
      }
      
      // Check if dropdown would go off-screen to the bottom
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4; // Position above button
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      
      // Ensure dropdown doesn't go off-screen to the left
      if (x < 0) {
        x = 8;
      }
      
      // Ensure dropdown doesn't go off-screen to the top
      if (y < 0) {
        y = 8;
      }
      
      setDropdownCoords({ x, y });
    }
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);


  const fetchApplications = useCallback(async (page = 1, search = searchQuery) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Build query parameters for search API
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', '15');
      
      // Add search query if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const queryString = params.toString();
      const response = await axios.get(`https://hrmspayroll.prepstripe.com/api/applications/search?${queryString}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Handle search API response structure: response.data.data.data for array
      const applicationsData = response.data?.data?.data || response.data?.data || [];
      setApplications(applicationsData);
      
      // Handle pagination from search API response
      const responseData = response.data?.data || {};
      const totalPages = responseData.last_page || Math.ceil((responseData.total || 0) / (responseData.per_page || 15)) || 1;
      
      setPagination({
        page: responseData.current_page || page,
        totalPages: totalPages
      });
    } catch (error) {
      setNotification({ message: error?.response?.data?.message || 'Failed to fetch applications.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const fetchApplicationDetails = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`https://hrmspayroll.prepstripe.com/api/applications/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const applicationData = response.data.data || response.data;
      // console.log('Application Details:', applicationData);
      // console.log('Full Name:', applicationData.full_name);
      setSelectedApp(applicationData);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error('Error fetching application details:', error);
      setNotification({ message: error?.response?.data?.message || 'Failed to fetch details.', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`https://hrmspayroll.prepstripe.com/api/applications/${deleteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setNotification({ message: 'Application deleted successfully.', type: 'success' });
      fetchApplications(currentPage, searchQuery);
    } catch (error) {
      setNotification({ message: error?.response?.data?.message || 'Failed to delete application.', type: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteId(null);
  };

  const handleEdit = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`https://hrmspayroll.prepstripe.com/api/applications/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setEditForm(response.data.data || response.data);
      setEditFormErrors({});
      setShouldDeleteResume(false);
      if (editResumeInputRef.current) editResumeInputRef.current.value = '';
      setIsEditModalOpen(true);
    } catch (error) {
      setNotification({ message: error?.response?.data?.message || 'Failed to fetch application for update.', type: 'error' });
    }
  };

  const fetchCampaignDetails = useCallback(async (campaignId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`https://hrmspayroll.prepstripe.com/api/campaigns/${campaignId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const campaignData = response.data.data || response.data;
      setSelectedCampaign(campaignData);
      setIsCampaignDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      setNotification({ message: error?.response?.data?.message || 'Failed to fetch campaign details.', type: 'error' });
    }
  }, []);

  const closeCampaignDrawer = () => {
    setIsCampaignDrawerOpen(false);
    setSelectedCampaign(null);
  };

  const handleEditChange = (e) => {
    const { name, value, type, files } = e.target;
    
    // Restrict phone number to only numbers and max 10 digits
    // Restrict PAN to uppercase alphanumeric and max 10 characters
    // Restrict Aadhaar to only numbers and max 12 digits
    let processedValue = value;
    if (name === 'phone_number') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'pan_number') {
      processedValue = value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10);
    } else if (name === 'aadhaar_number') {
      processedValue = value.replace(/\D/g, '').slice(0, 12);
    }
    
    setEditForm(prev => ({
      ...prev,
      [name]:
        name === 'comfortable_travel' || name === 'comfortable_six_days'
          ? processedValue === '1'
          : type === 'file'
            ? files[0]
            : processedValue
    }));
    
    // Real-time validation for 50 character limit fields
    const fieldsWith50CharLimit = ['full_name', 'current_location', 'current_company_name', 'current_designation', 'notice_period', 'highest_education'];
    const fieldDisplayNames = {
      'full_name': 'Full name',
      'current_location': 'Current location',
      'current_company_name': 'Current company name',
      'current_designation': 'Current designation',
      'notice_period': 'Notice period',
      'highest_education': 'Highest education'
    };
    
    if (fieldsWith50CharLimit.includes(name)) {
      if (processedValue.length > 50) {
        setEditFormErrors(prev => ({
          ...prev,
          [name]: `${fieldDisplayNames[name]} cannot exceed 50 characters`
        }));
      } else {
        setEditFormErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    } else {
      // Clear error when user starts typing for other fields
      if (editFormErrors[name]) {
        setEditFormErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.full_name?.trim()) errors.full_name = 'Full name is required';
    if (!editForm.phone_number?.trim()) errors.phone_number = 'Phone number is required';
    if (!editForm.email?.trim()) errors.email = 'Email is required';
    if (!editForm.pan_number?.trim()) errors.pan_number = 'PAN number is required';
    if (!editForm.aadhaar_number?.trim()) errors.aadhaar_number = 'Aadhaar number is required';
    if (!editForm.date_of_birth?.trim()) errors.date_of_birth = 'Date of birth is required';
    if (!editForm.total_experience_years) errors.total_experience_years = 'Total experience is required';
    if (!editForm.expected_ctc_month) errors.expected_ctc_month = 'Expected CTC is required';
    if (!editForm.source_of_application?.trim()) errors.source_of_application = 'Source of application is required';
    
    // Character limit validations (50 characters) - only check if value exists and exceeds limit
    if (editForm.full_name && editForm.full_name.length > 50) {
      errors.full_name = 'Full name cannot exceed 50 characters';
    }
    if (editForm.current_location && editForm.current_location.length > 50) {
      errors.current_location = 'Current location cannot exceed 50 characters';
    }
    if (editForm.current_company_name && editForm.current_company_name.length > 50) {
      errors.current_company_name = 'Current company name cannot exceed 50 characters';
    }
    if (editForm.current_designation && editForm.current_designation.length > 50) {
      errors.current_designation = 'Current designation cannot exceed 50 characters';
    }
    if (editForm.notice_period && editForm.notice_period.length > 50) {
      errors.notice_period = 'Notice period cannot exceed 50 characters';
    }
    if (editForm.highest_education && editForm.highest_education.length > 50) {
      errors.highest_education = 'Highest education cannot exceed 50 characters';
    }
    
    // Resume validation - required if deleting current resume or no resume exists
    const hasExistingResume = (typeof editForm.resume === 'string' && editForm.resume && editForm.resume !== 'null') || 
                               (typeof editForm.resume_url === 'string' && editForm.resume_url && editForm.resume_url !== 'null');
    
    if (shouldDeleteResume && !editForm.resume) {
      errors.resume = 'Please upload a new resume file';
    } else if (editForm.resume && editForm.resume instanceof File) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(editForm.resume.type)) {
        errors.resume = 'Only PDF, DOC, or DOCX files are allowed';
      }
    } else if (!hasExistingResume && !editForm.resume) {
      errors.resume = 'Resume file is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (editForm.email && !emailRegex.test(editForm.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone number validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (editForm.phone_number && !phoneRegex.test(editForm.phone_number)) {
      errors.phone_number = 'Phone number must be exactly 10 digits';
    }

    // PAN number validation (format: ABCDE1234F - 5 letters, 4 digits, 1 letter)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (editForm.pan_number && !panRegex.test(editForm.pan_number)) {
      errors.pan_number = 'PAN number must be in format ABCDE1234F (5 letters, 4 digits, 1 letter)';
    }

    // Aadhaar number validation (exactly 12 digits)
    const aadhaarRegex = /^\d{12}$/;
    if (editForm.aadhaar_number && !aadhaarRegex.test(editForm.aadhaar_number)) {
      errors.aadhaar_number = 'Aadhaar number must be exactly 12 digits';
    }

    // Date of birth validation
    if (editForm.date_of_birth) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(editForm.date_of_birth)) {
        errors.date_of_birth = 'Date must be in YYYY-MM-DD format';
      } else {
        const date = new Date(editForm.date_of_birth);
        const today = new Date();
        if (date > today) {
          errors.date_of_birth = 'Date of birth cannot be in the future';
        }
      }
    }

    // Number validations
    if (editForm.total_experience_years && editForm.total_experience_years < 0) {
      errors.total_experience_years = 'Experience cannot be negative';
    }
    if (editForm.current_ctc_month && editForm.current_ctc_month < 0) {
      errors.current_ctc_month = 'Current CTC cannot be negative';
    }
    if (editForm.expected_ctc_month && editForm.expected_ctc_month < 0) {
      errors.expected_ctc_month = 'Expected CTC cannot be negative';
    }

    return errors;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus();
          }
        }
      }, 100);
      return;
    }

    setIsSubmittingEdit(true);
    
    try {
      const token = localStorage.getItem('authToken');
      let dataToSend = editForm;
      let config = {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      };
      // If a new resume file is selected or resume should be deleted, use FormData
      if ((editForm.resume && editForm.resume instanceof File) || shouldDeleteResume) {
        const formData = new FormData();
        Object.entries(editForm).forEach(([key, value]) => {
          if (key === 'resume' && value instanceof File) {
            formData.append('resume', value);
          } else if (key === 'comfortable_travel' || key === 'comfortable_six_days') {
            formData.append(key, value ? '1' : '0');
          } else if (key !== 'resume' && key !== 'resume_url') {
            // Don't send resume/resume_url fields if not a file
            formData.append(key, value || '');
          }
        });
        // If resume should be deleted, append a flag or empty file
        if (shouldDeleteResume && !(editForm.resume && editForm.resume instanceof File)) {
          formData.append('delete_resume', '1');
        }
        dataToSend = formData;
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        // For regular JSON updates, also convert booleans to '0'/'1'
        const jsonData = { ...editForm };
        // Remove resume fields from JSON if not being updated
        delete jsonData.resume;
        delete jsonData.resume_url;
        if (jsonData.comfortable_travel !== undefined) {
          jsonData.comfortable_travel = jsonData.comfortable_travel ? '1' : '0';
        }
        if (jsonData.comfortable_six_days !== undefined) {
          jsonData.comfortable_six_days = jsonData.comfortable_six_days ? '1' : '0';
        }
        dataToSend = jsonData;
      }
      await axios.post(`https://hrmspayroll.prepstripe.com/api/applications/${editForm.id}`, dataToSend, config);
      setNotification({ 
        message: 'Application updated successfully!', 
        type: 'success' 
      });
      setIsEditModalOpen(false);
      setEditFormErrors({});
      setShouldDeleteResume(false);
      if (editResumeInputRef.current) editResumeInputRef.current.value = '';
      fetchApplications(currentPage, searchQuery);
    } catch (error) {
      console.error('Error updating application:', error);
      
      // Handle validation errors from server (422 or similar status codes)
      if (error.response?.data?.errors) {
        const serverErrors = {};
        Object.entries(error.response.data.errors).forEach(([key, value]) => {
          serverErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setEditFormErrors(serverErrors);
        
        // Scroll to first error field after a short delay to ensure DOM is updated
        setTimeout(() => {
          const firstErrorField = Object.keys(serverErrors)[0];
          if (firstErrorField) {
            const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              errorElement.focus();
            }
          }
        }, 100);
        
        // Don't show error modal for validation errors - they're shown inline
        return;
      }
      
      // Handle other error types
      let errorMsg = 'Failed to update application. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMsg = error.response.data?.message || 
                   error.response.data?.error || 
                   `Server error (${error.response.status}). Please try again.`;
      } else if (error.request) {
        // Request was made but no response received
        errorMsg = 'Network error. Please check your internet connection and try again.';
      } else {
        // Something else happened
        errorMsg = error.message || 'An unexpected error occurred. Please try again.';
      }
      
      setNotification({ message: errorMsg, type: 'error' });
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Debounced search effect - resets to page 1 when search changes
  useEffect(() => {
    // Skip debounce on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const debounceTimer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchApplications(1, searchQuery);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, fetchApplications]);

  // Fetch applications when page changes or on initial mount
  useEffect(() => {
    fetchApplications(currentPage, searchQuery);
    // eslint-disable-next-line
  }, [currentPage]);

  const columns = useMemo(() => [
    { 
      label: "S.No", 
      field: "serial_number", 
      type: "text", 
      render: (row, idx) => {
        const serialNumber = (currentPage - 1) * 15 + idx + 1;
        return <span className="text-gray-700 dark:text-gray-300 font-medium">{serialNumber}</span>;
      }
    },
    { label: "Full Name", field: "full_name", type: "text", render: row => (
      <button 
        onClick={() => fetchApplicationDetails(row.id)}
        className="text-blue-600 hover:underline hover:underline-offset-4 font-medium"
      >
        {row.full_name || '-'}
      </button>
    )},
    { label: "Email", field: "email", type: "text" },
    { label: "Phone", field: "phone_number", type: "text" },
    { 
      label: "Campaign Name", 
      field: "campaign_name", 
      type: "text", 
      render: row => {
        const campaignId = row.campaign_id || row.campaign?.id;
        const campaignName = row.campaign_name || row.campaign?.campaign_name || '-';
        
        if (!campaignId || campaignName === '-') {
          return <span className="text-gray-500">{campaignName}</span>;
        }
        
        const campaignDetailsPath = getRoleBasedRoute(
          `/campaigns/${campaignId}`,
          roles
        );
        
        return (
          <div className="flex items-center gap-1">
            <Link
              to={campaignDetailsPath}
              state={{ fromApplicationResponses: true }}
              className="text-blue-600 hover:underline dark:text-white hover:underline-offset-4 cursor-pointer font-medium"
              aria-label={`View details for ${campaignName}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {campaignName}
            </Link>
          </div>
        );
      }
    },
    { label: "Submitted At", field: "created_at", type: "text", render: row => {
      if (!row.created_at) return '-';
      const date = new Date(row.created_at);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    }},
    {
      label: "Actions", field: "actions", type: "text", render: row => (
        <div className="flex items-center justify-center">
          <div className="relative dropdown-container">
            <button
              ref={el => { if (el) buttonRefs.current[row.id] = el; }}
              onClick={e => handleDropdownToggle(row.id, e)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            {openDropdownId === row.id && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: dropdownCoords.y,
                  left: dropdownCoords.x,
                  zIndex: 9999,
                }}
                className="dropdown-menu bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[150px]"
              >
                <div className="flex flex-col space-y-0">
                  <button
                    onClick={() => { fetchApplicationDetails(row.id); setOpenDropdownId(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => { handleEdit(row.id); setOpenDropdownId(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    Update
                  </button>
                  {/* Delete option commented out - removed from actions dropdown */}
                  {/* <DeleteButton 
                    style={`w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20`}
                    onDelete={() => { handleDelete(row.id); setOpenDropdownId(null); }}
                  /> */}
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      )
    }
  ], [isDeleting, openDropdownId, dropdownCoords, roles, fetchApplicationDetails, fetchCampaignDetails, currentPage]);

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage);
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Application Responses</h2>
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Search Controls */}
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">
        {/* Search field */}
        <div className="w-full sm:w-64">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full placeholder:text-xs dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 md:placeholder:text-sm p-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>

      <ReusableTable
        data={applications}
        loading={loading}
        columns={columns}
        filterable={false}
      />

      {applications.length > 0 && (
        <div className="mt-4">
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        </div>
      )}

      {/* Details Drawer */}
      <Drawer
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        border_radius="md:rounded-none"
        showClose={false}
        ChilDiv=""
        ChildParent="">
        <ApplicationDetails 
          application={selectedApp} 
          close={() => setIsDetailsModalOpen(false)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewResume={(url) => {
            setResumeUrl(url);
            setIsResumeModalOpen(true);
          }}
        />
      </Drawer>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => {
        setIsEditModalOpen(false);
        setEditFormErrors({});
      }} title="Update Application">
        {editForm ? (
          <form onSubmit={handleEditSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={editForm.full_name ?? ''}
                  onChange={handleEditChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.full_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {editFormErrors.full_name && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.full_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone_number"
                  value={editForm.phone_number ?? ''}
                  onChange={handleEditChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.phone_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {editFormErrors.phone_number && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.phone_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email ?? ''}
                  onChange={handleEditChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email address"
                />
                {editFormErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PAN Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="pan_number"
                  value={editForm.pan_number ?? ''}
                  onChange={handleEditChange}
                  maxLength="10"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.pan_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ABCDE1234F"
                />
                {editFormErrors.pan_number && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.pan_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={editForm.date_of_birth ?? ''}
                  onChange={handleEditChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.date_of_birth ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.date_of_birth && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.date_of_birth}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total Experience (Years) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="total_experience_years"
                  value={editForm.total_experience_years ?? ''}
                  onChange={handleEditChange}
                  min="0"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.total_experience_years ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter years of experience"
                />
                {editFormErrors.total_experience_years && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.total_experience_years}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Location
                </label>
                <input
                  type="text"
                  name="current_location"
                  value={editForm.current_location ?? ''}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.current_location ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Pune, India"
                />
                {editFormErrors.current_location && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.current_location}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Aadhaar Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="aadhaar_number"
                  value={editForm.aadhaar_number ?? ''}
                  onChange={handleEditChange}
                  maxLength="12"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.aadhaar_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter 12-digit Aadhaar number"
                />
                {editFormErrors.aadhaar_number && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.aadhaar_number}</p>
                )}
              </div>
            </div>

            {/* Education & Current Job */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Highest Education
                </label>
                <input
                  type="text"
                  name="highest_education"
                  value={editForm.highest_education ?? ''}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.highest_education ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., MBA, B.Tech, etc."
                />
                {editFormErrors.highest_education && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.highest_education}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Company Name
                </label>
                <input
                  type="text"
                  name="current_company_name"
                  value={editForm.current_company_name ?? ''}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.current_company_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter current company name"
                />
                {editFormErrors.current_company_name && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.current_company_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Designation
                </label>
                <input
                  type="text"
                  name="current_designation"
                  value={editForm.current_designation ?? ''}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.current_designation ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter current designation"
                />
                {editFormErrors.current_designation && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.current_designation}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current CTC (Monthly)
                </label>
                <input
                  type="number"
                  name="current_ctc_month"
                  value={editForm.current_ctc_month ?? ''}
                  onChange={handleEditChange}
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.current_ctc_month ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter current monthly CTC"
                />
                {editFormErrors.current_ctc_month && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.current_ctc_month}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Expected CTC (Monthly) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="expected_ctc_month"
                  value={editForm.expected_ctc_month ?? ''}
                  onChange={handleEditChange}
                  min="0"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.expected_ctc_month ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter expected monthly CTC"
                />
                {editFormErrors.expected_ctc_month && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.expected_ctc_month}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notice Period
                </label>
                <input
                  type="text"
                  name="notice_period"
                  value={editForm.notice_period ?? ''}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.notice_period ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 2 months"
                />
                {editFormErrors.notice_period && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.notice_period}</p>
                )}
              </div>
            </div>

            {/* Application Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Source of Application <span className="text-red-500">*</span>
                </label>
                <select
                  name="source_of_application"
                  value={editForm.source_of_application ?? ''}
                  onChange={handleEditChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.source_of_application ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select source</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Indeed">Indeed</option>
                  <option value="Naukri">Naukri</option>
                  <option value="Referral">Referral</option>
                  <option value="Company Website">Company Website</option>
                  <option value="Other">Other</option>
                </select>
                {editFormErrors.source_of_application && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.source_of_application}</p>
                )}
              </div>
            </div>

            {/* Yes/No Dropdowns for Comfortable Travel and Six Days */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comfortable with travel requirements
                </label>
                <select
                  name="comfortable_travel"
                  value={editForm.comfortable_travel ? '1' : '0'}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.comfortable_travel ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
                {editFormErrors.comfortable_travel && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.comfortable_travel}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comfortable with six-day work week
                </label>
                <select
                  name="comfortable_six_days"
                  value={editForm.comfortable_six_days ? '1' : '0'}
                  onChange={handleEditChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    editFormErrors.comfortable_six_days ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
                {editFormErrors.comfortable_six_days && (
                  <p className="text-red-500 text-sm mt-1">{editFormErrors.comfortable_six_days}</p>
                )}
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resume {!shouldDeleteResume && (!editForm.resume_url && !editForm.resume) ? <span className="text-red-500">*</span> : null}
              </label>
              {!shouldDeleteResume && ((typeof editForm.resume === 'string' && editForm.resume && editForm.resume !== 'null') || 
               (typeof editForm.resume_url === 'string' && editForm.resume_url && editForm.resume_url !== 'null')) ? (
                <div className="mb-3">
                  <a href={editForm.resume_url || editForm.resume} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mb-2 block">View Current Resume</a>
                  <button
                    type="button"
                    onClick={() => {
                      setShouldDeleteResume(true);
                      setEditForm(prev => ({ ...prev, resume: null }));
                      if (editResumeInputRef.current) editResumeInputRef.current.value = '';
                    }}
                    className="text-red-600 hover:text-red-800 text-sm underline"
                  >
                    Delete Current Resume
                  </button>
                </div>
              ) : null}
              {shouldDeleteResume && (
                <p className="text-orange-600 text-sm mb-2">Current resume will be deleted. Please upload a new resume.</p>
              )}
              <input
                type="file"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleEditChange}
                ref={editResumeInputRef}
                required={shouldDeleteResume || (!editForm.resume_url && !editForm.resume)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                  editFormErrors.resume ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {editForm.resume && editForm.resume instanceof File && (
                <p className="text-green-600 text-sm mt-1">Selected: {editForm.resume.name}</p>
              )}
              {shouldDeleteResume && (
                <button
                  type="button"
                  onClick={() => {
                    setShouldDeleteResume(false);
                    if (editResumeInputRef.current) editResumeInputRef.current.value = '';
                    setEditForm(prev => ({ ...prev, resume: null }));
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm mt-2 underline"
                >
                  Cancel - Keep Current Resume
                </button>
              )}
              {editFormErrors.resume && (
                <p className="text-red-500 text-sm mt-1">{editFormErrors.resume}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={isSubmittingEdit}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingEdit ? 'Updating...' : 'Update Application'}
              </button>
            </div>
          </form>
        ) : <div>Loading...</div>}
      </Modal>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Application"
        message="Are you sure you want to delete this application? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Campaign Details Drawer */}
      <Drawer
        isOpen={isCampaignDrawerOpen}
        onClose={closeCampaignDrawer}
        border_radius="md:rounded-md"
        showClose={false}
        ChilDiv=""
        ChildParent=""
      >
        <CampaignDetails
          campaign={selectedCampaign}
          campaign_id={selectedCampaign?.id}
          close={closeCampaignDrawer}
        />
      </Drawer>

      {/* Resume Modal with Print */}
      <Modal 
        isOpen={isResumeModalOpen} 
        onRequestClose={() => {
          setIsResumeModalOpen(false);
          setResumeUrl(null);
        }} 
        title="Resume"
        customZIndex={10000}
        fullSize={true}
      >
        {resumeUrl && (
          <div className="w-full h-[95vh]">
            <iframe
              ref={resumeIframeRef}
              src={resumeUrl}
              className="w-full h-full border-0 rounded-lg"
              title="Resume"
              onLoad={() => {
                // Try to trigger print after iframe loads
                setTimeout(() => {
                  try {
                    if (resumeIframeRef.current && resumeIframeRef.current.contentWindow) {
                      resumeIframeRef.current.contentWindow.print();
                    }
                  } catch (error) {
                    // If CORS blocks it, show a message or provide download option
                    console.warn('Cannot print due to CORS restrictions. You can use browser print (Ctrl/Cmd + P) or download the file.');
                  }
                }, 1000);
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApplicationResponses; 